import * as React from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import PrimaryButton from '../buttons/PrimaryButton';
import SecondaryButton from '../buttons/SecondaryButton';
import { useAsyncAction } from '../../../commonComponents/Hooks';
import Loading from '../../../commonComponents/Loading';
import MessageBox from '../MessageBox';
import Buttons from '../Buttons';
import { deepCopy, getNestedFieldValue, hasValue } from '../../../lib/utils';
import { red } from '../../cssConstants';

const GenericForm = (props: Props) => {
	const [loading, errorMessage, performAsyncAction] = useAsyncAction();

	const { defaultValues, onSubmission: handleSubmission, children, onBackClick, isAccountView } = props;
	const methods = useForm({
		mode: 'all',
		defaultValues,
		// The common rules (required / pattern / maxLength) declared on each field must always run. Previously they
		// were only applied when a `validate` prop was supplied, which silently disabled all field validation on
		// every form that did not pass one.
		resolver: (values: any, _, options) => {
			const commonErrors = validateCommonRules(options.fields, values);
			const bespokeErrors = props.validate ? props.validate(values) : {};
			const errors = { ...commonErrors, ...bespokeErrors };
			Object.entries(errors).forEach(([key, value]) => {
				if (!hasValue(value)) {
					delete errors[key];
				}
			});
			return { errors, values };
		}
	});
	const { reset, handleSubmit, getValues } = methods;

	const handleDelete = () => {
		const values = deepCopy(getValues());
		props.onDeleteClick(values);
	};

	const submitData = async () => {
		// We must grab the values now and clone them before performAsyncAction runs because showing the loader will cause
		// some fields to be demounted, which will cause the related fields to be deregistered.
		const values = deepCopy(getValues());

		// performAsyncAction swallows the error so that it can display it, which means a failed save would otherwise
		// look identical to a successful one from here. It reports the message back instead, so the user can always
		// be told the save did not happen even when the inline message is rendered off-screen up a long form.
		const failureMessage = await performAsyncAction(async () => {
			await handleSubmission(values);
			if (isAccountView) {
				reset(values);
			} else {
				reset(defaultValues);
			}
		});

		if (hasValue(failureMessage)) {
			toast.error(failureMessage, { bodyClassName: 'error wide', autoClose: 8000 });
		}
	};

	const handleInvalidSubmit = (fieldErrors: FieldErrors) => {
		toast.error('Please correct the highlighted fields before saving.');

		// On a long form the rejected field is usually off-screen, so the inline error alone is easy to miss.
		// FieldWrapper tags each field with data-fieldname, which lets us bring the first one into view.
		const [firstFieldName] = Object.keys(fieldErrors);
		if (!hasValue(firstFieldName)) return;

		const element = document.querySelector(`[data-fieldname="${CSS.escape(firstFieldName)}"]`);
		element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
	};

	return (
		// eslint-disable-next-line react/jsx-props-no-spreading
		<FormProvider {...methods}>
			{loading && <Loading loadingPosition="fixed" usePortal />}
			<form onSubmit={handleSubmit(submitData, handleInvalidSubmit)}>
				<MessageBox error={errorMessage} />
				{children}

				<Buttons centre={props.buttonsCentre} left={props.buttonsLeftAligned}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
						{props.additionalButtons && <div style={{ marginRight: 'auto' }}>{props.additionalButtons}</div>}
						<div style={{ display: 'flex', gap: '0.5rem' }}>
							{props.showDeleteButton && (
								<SecondaryButton color={red} text="Delete" fontColor="white" onClick={handleDelete} />
							)}
							{props.isShownSecondaryButton && <SecondaryButton text="Back" onClick={onBackClick} />}
							<PrimaryButton
								disabled={loading || props.primaryButtonDisabled}
								text={props.primaryButtonText}
								type="submit"
							/>
						</div>
						{props.farRightButton && <div style={{ marginLeft: 'auto' }}>{props.farRightButton}</div>}
					</div>
				</Buttons>
			</form>
		</FormProvider>
	);
};

// TODO: figure out how to replace these anys.
interface Props {
	defaultValues: any;
	onSubmission: (data: any) => Promise<void>;
	onBackClick?: () => void;
	children: JSX.Element | JSX.Element[];
	validate?: (values: any) => Record<string, Record<string, string>>;
	primaryButtonText?: string;
	isShownSecondaryButton?: boolean;
	buttonsCentre?: boolean;
	buttonsLeftAligned?: boolean;
	isAccountView?: boolean;
	showDeleteButton?: boolean;
	onDeleteClick?: (data: any) => Promise<void>;
	primaryButtonDisabled?: boolean;
	additionalButtons?: JSX.Element;
	farRightButton?: JSX.Element;
}

GenericForm.defaultProps = {
	validate: null,
	primaryButtonText: 'Save',
	isShownSecondaryButton: true,
	onBackClick: () => {},
	buttonsCentre: false,
	buttonsLeftAligned: false,
	isAccountView: false,
	showDeleteButton: false,
	onDeleteClick: () => {},
	primaryButtonDisabled: false,
	additionalButtons: null
};

export default GenericForm;

const validateCommonRules = (fields: Record<string, unknown>, values: Record<string, unknown>) => {
	let errors = {} as Record<string, unknown>;
	// eslint-disable-next-line no-restricted-syntax
	for (const fieldName in fields) {
		if (Object.prototype.hasOwnProperty.call(fields, fieldName)) {
			if (Array.isArray(fields[fieldName])) {
				const array = fields[fieldName] as Array<unknown>;
				for (let i = 0; i < array.length; i++) {
					const element = array[i] as Record<string, unknown>;
					const elementErrors = validateCommonRules(element, values);
					errors = { ...errors, ...elementErrors };
				}
			} else if (fields[fieldName] === undefined) {
				// For some reason in the most recent versions of react-hook-form, array fields are undefined
				// when the first array element is added.
				// TODO: check sometime in the future if this bug is fixed.
				console.warn(`The ${fieldName} field is undefined.`);
			} else {
				const field = fields[fieldName] as any;
				if (field.name !== undefined && field.ref !== undefined) {
					errors[field.name] = validateFieldWithCommonRules(field, getNestedFieldValue(values, field.name));
				} else {
					validateCommonRules(field, getNestedFieldValue(values, fieldName));
				}
			}
		}
	}
	return errors;
};

const validateFieldWithCommonRules = (field: any, fieldValue: any) => {
	const { required, pattern, maxLength } = field;
	if (hasValue(required) && required.value === true && !hasValue(fieldValue)) {
		return { type: 'required', message: required.message };
	}
	if (hasValue(pattern) && hasValue(fieldValue) && !pattern.value.test(fieldValue)) {
		return { type: 'pattern', message: pattern.message };
	}
	if (hasValue(maxLength) && hasValue(fieldValue) && fieldValue.length > maxLength.value) {
		return { type: 'maxLength', message: maxLength.message };
	}
	return undefined;
};
